import { endOfMonth, getWeeksInMonth } from '@internationalized/date';
import { useCalendarGrid } from '@react-aria/calendar';
import { useLocale } from '@react-aria/i18n';
import cn from 'classnames';
import { forwardRef } from 'react';

import { CalendarCell } from '../calendar-cell';

import type { ForwardedRef, ReactElement } from 'react';

import type { CalendarGridProps } from './calendar-grid.types';

import './calendar-grid.scss';

/**
 * The component is supposed to be used as a child of the `Calendar` component.
 * @internal
 */
const CalendarGrid = forwardRef(
  (props: CalendarGridProps, forwardedRef: ForwardedRef<HTMLUListElement> | null): ReactElement => {
    const {
      className,
      locale: localeProp,
      offset = 0,
      state,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const { locale: defaultLocale } = useLocale();
    const locale = localeProp ?? defaultLocale;

    const startDate = state.visibleRange.start.add({ months: offset });
    const endDate = endOfMonth(startDate);

    const { gridProps, weekDays } = useCalendarGrid(
      {
        endDate,
        startDate,
        // Doing this to get full days for the keys
        weekdayStyle: 'short',
      },
      state,
    );

    const weeksInMonthStart = getWeeksInMonth(startDate, locale);
    const weeksInMonthEnd = getWeeksInMonth(endDate, locale);
    const weeksInMonth = Math.max(weeksInMonthStart, weeksInMonthEnd);

    return (
      <ul className={cn('rbui-calendar-grid', className)} {...gridProps} ref={forwardedRef} {...rest}>
        {weekDays.map((day) => (
          <li className="rbui-calendar-cell__container" key={day}>
            <span className="rbui-calendar-cell rbui-calendar-cell--week">{day.charAt(0).toUpperCase()}</span>
          </li>
        ))}

        {[...Array.from({ length: weeksInMonth }).keys()].reduce<ReactElement[]>((acc, weekIndex) => {
          return [
            ...acc,
            ...state
              .getDatesInWeek(weekIndex, startDate)
              .map((date) => (
                <CalendarCell currentMonth={startDate} date={date!} key={`${weekIndex}-${date?.day}`} state={state} />
              ))
              .filter((element): element is ReactElement => element !== null),
          ];
        }, [])}
      </ul>
    );
  },
);

CalendarGrid.displayName = 'CalendarGrid';

export default CalendarGrid;
